import React, { useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import moment from 'moment';

import { generateFullName } from 'utils/generateFullNameUtils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useLeadIntake } from 'modules/lead/hooks/useLeadIntake';
import { dateFormatMap } from 'constants/dropdownOptions';
import constants from 'constants/index';
import { generateFormPdf, getPropByString, formatDateWithoutTimezone } from 'utils/utils';
import { getLeadDetailsService } from 'services/intakeforms/leadIntakeService';
import Cookies from 'universal-cookie';

const formatValue = ({ value, dateFormat, type, time, period, _practice_area_ }) => {
  let displayValue = '-';
  if (type === 'practice_area') {
    return _practice_area_ || value || '-';
  } else if (type === 'time') {
    if (time && period) {
      displayValue = `${time} ${period}`;
    }
  } else if (Array.isArray(value)) {
    displayValue = value?.join(', ').replace(/_/g, ' ');
  } else if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (Boolean(dateFormat) || type === 'Date') {
    displayValue = value ? moment(value).format(dateFormatMap.get(dateFormat) ?? 'MM/DD/YYYY') : '-';
  } else if ((typeof value === 'number' || typeof value === 'string') && value !== '') {
    displayValue = String(value);
  }
  return displayValue;
};

// Function to format US phone numbers
const formatUSPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '-';

  // Remove all non-digit characters
  let cleaned = phoneNumber.replace(/\D/g, '');

  // If empty after cleaning, return original or dash
  if (cleaned.length === 0) return phoneNumber || '-';

  // Handle different scenarios
  if (cleaned.length === 10) {
    // Standard 10-digit US number
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // 11-digit number starting with 1
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 11 && !cleaned.startsWith('1')) {
    // 11-digit number not starting with 1, assume it's a valid US number
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 7) {
    // 7-digit number (local number)
    return `(555) 1234`;
  } else {
    // For any other length, return formatted as best we can
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
};

export default function CaptureNowTable() {
  const [expandedRows, setExpandedRows] = useState(null);
  const userContext = useUserDetailsContext();
  const cookie = new Cookies();

  // Use the same hook but with lead_type='capturenow'
  const { leadForms, paginatorTemplate, pageSize, acceptIntake, rejectIntake, fetchLeadDetails, leadDetails, loadingDetails } =
    useLeadIntake(10, 'capturenow');

  // Handle row expansion
  const handleRowToggle = (e) => {
    setExpandedRows(e.data);

    // Fetch details for newly expanded rows
    e.data.forEach((expandedLead) => {
      const leadId = expandedLead?.capturenow_lead_id || expandedLead?.sk || expandedLead?.id;
      if (leadId && !leadDetails[leadId] && !loadingDetails[leadId]) {
        fetchLeadDetails(expandedLead);
      }
    });
  };

  const approveIntake = async (data) => {
    const leadId = data?.capturenow_lead_id || data?.sk || data?.id;
    let details = leadDetails[leadId];

    // If we don't have details yet, fetch them first
    if (!details && !loadingDetails[leadId]) {
      try {
        // Fetch details and get the response directly
        const tenantId = cookie.get('tenantId');
        if (tenantId && leadId) {
          const response = await getLeadDetailsService(tenantId, leadId, 'capturenow');
          details = response?.data || response;
        }
      } catch (error) {
        console.error('Error fetching lead details for approval:', error);
      }
    }

    const _data = {
      ...data,
      contact_data: {
        first_name: data?.full_name?.split(' ')?.[0] || '',
        last_name: data?.full_name?.split(' ')?.[1] || '',
        email: data?.email,
        phone: data?.phone_number_provided ? data?.phone_number_provided : data?.phone_number_system,
      },
      // Include transcript summary as description for CaptureNow leads
      transcript_summary: details?.transcript_summary || details?.case_summary || details?.summary || '',
    };

    confirmDialog({
      message: 'Are you sure you want to create this lead as a draft?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        acceptIntake(_data);
      },
      reject: () => null,
    });
  };

  const expandedRowTemplate = (data) => {
    const leadId = data?.capturenow_lead_id || data?.sk || data?.id;
    const details = leadDetails[leadId];
    const isLoading = loadingDetails[leadId];
    const otherFields = data?.form_data?.filter((v) => !(v?.res_type === 'custom-field' || v?.field_type === 'contact'));

    return (
      <div className="card-bg p-3">
        {/* Summary Section from API */}
        <h5 className="sub-title mb-3">Summary</h5>
        <div className="dynamic-labels-container">
          {isLoading ? (
            <div className="text-muted">
              <i className="pi pi-spinner pi-spin me-2"></i>
              Loading summary...
            </div>
          ) : details?.error ? (
            <div className="text-danger">
              <i className="pi pi-exclamation-triangle me-2"></i>
              {details.error}
            </div>
          ) : details ? (
            <div>
              <span>{details?.case_summary || details?.transcript_summary || 'No summary available'}</span>
            </div>
          ) : (
            <div className="text-muted">No summary data available</div>
          )}
        </div>

        <h5 className="sub-title mt-3">Contact Data</h5>
        <div className="dynamic-labels-container">
          <div className="label-value-pair">
            <span className="p-medium">Name</span>
            <span>{data?.full_name || generateFullName(data?.contact_data)}</span>
          </div>
          {data?.contact_data?.dob && (
            <div className="label-value-pair">
              <span className="p-medium">Date of Birth</span>
              <span>{formatDateWithoutTimezone(data?.contact_data?.dob)}</span>
            </div>
          )}
          {data?.email && (
            <div className="label-value-pair">
              <span className="p-medium">Email</span>
              <span>{data?.email}</span>
            </div>
          )}
          {data?.phone_number_system ||
            (data?.phone_number_provided && (
              <div className="label-value-pair">
                <span className="p-medium">Phone</span>
                <span>{formatUSPhoneNumber(data?.phone_number_provided ? data?.phone_number_provided : data?.phone_number_system)}</span>
              </div>
            ))}
          {data?.contact_data?.additional_phone && (
            <div className="label-value-pair">
              <span className="p-medium">Additional Phone</span>
              <span>{formatUSPhoneNumber(data?.contact_data?.additional_phone)}</span>
            </div>
          )}
          {data?.form_data
            ?.filter((v) => v?.res_type === 'custom-field')
            ?.map((item, index) => (
              <div key={index} className="label-value-pair">
                <span className="p-medium">{item?.label || item?.name}:</span>
                <span>{formatValue(item)}</span>
              </div>
            ))}
        </div>

        {/* Other Fields Section */}
        {otherFields?.length > 0 && (
          <>
            <h5 className="sub-title mt-3">Other Fields</h5>
            <div className="dynamic-labels-container">
              {otherFields?.map((item, index) => (
                <div key={index} className="label-value-pair">
                  <span className="p-medium">{item?.label || item?.name}:</span>
                  <span>{formatValue(item)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const nameTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Name</span>
        <div className="d-inline-table">{rowData?.full_name || generateFullName(rowData?.contact_data)}</div>
      </>
    );
  };

  const phoneTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Phone Number</span>
        <div className="d-inline-table">
          <div className="d-flex align-items-center">
            <i className="pi pi-phone me-2 text-muted"></i>
            {formatUSPhoneNumber(rowData?.phone_number_provided ? rowData?.phone_number_provided : rowData?.phone_number_system)}
          </div>
        </div>
      </>
    );
  };

  const emailTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Email</span>
        <div className="d-inline-table">
          <div className="d-flex align-items-center">
            <i className="pi pi-envelope me-2 text-muted"></i>
            {rowData?.email || '-'}
          </div>
        </div>
      </>
    );
  };

  const actionBody = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Action</span>
        <div className="d-inline-table">
          <div className="d-flex align-items-center">
            <Button className="p-button p-button-secondary outline me-2" label="Accept" onClick={() => approveIntake(rowData)} />
            <a
              className="text-decoration-none pointer tooltip-intake danger-text"
              data-pr-tooltip="Delete"
              onClick={() => rejectIntake(rowData)}
            >
              <i className="icon-delete icon-red cursor-pointer ms-2" />
            </a>
          </div>
        </div>
      </>
    );
  };

  const renderDateContacted = (row, { field, header }) => (
    <>
      <span className="p-column-title text-break">{header}</span>
      <div className="d-inline-table intake-dates">
        <small>{convertDateFromApiToUserTimezone(getPropByString(row, field), userContext?.userDetails?.timezone)}</small>
      </div>
    </>
  );

  return (
    <div className="col-12 p-0 datatable-responsive">
      <DataTable
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        rowExpansionTemplate={expandedRowTemplate}
        value={leadForms || []}
        className="p-datatable-responsive"
        responsiveLayout="scroll"
        emptyMessage="No CaptureNow leads found."
        paginator
        rowHover
        paginatorTemplate={paginatorTemplate}
        rows={pageSize}
        paginatorClassName="mt-1"
        stripedRows
        storageKey="capturenow_leads_table"
      >
        <Column expander style={{ width: '5rem' }} />
        <Column header="Name" field="full_name" body={nameTemplate} />
        <Column header="Date Contacted" field="time" body={renderDateContacted} />
        <Column header="Phone Number" field="phone_number_provided ? phone_number_provided : phone_number_system" body={phoneTemplate} />
        <Column header="Email" field="email" body={emailTemplate} />
        <Column header="Actions" body={actionBody} />
      </DataTable>
    </div>
  );
}
